<template>
  <div class="row">
    <div class="col-sm-6">
      <div class="col-12">
        <DispatchForm v-if="formReseter" @draw-driver-on-map="findDriver" v-model:drop-off-data="dropOffData"
                      @reset-form="resetDispatchForm" v-model:pick-up-data="pickUpData" :service-provider-id="serviceProviderId"></DispatchForm>
      </div>
    </div>

    <div class="col-sm-6">
      <!-- Map   -->
      <div class="col-12">
        <div class="card height-equal">
          <div id="map" style=" min-height: inherit; width: 100%;"></div>
        </div>
      </div>
    </div>

    <div class="col-sm-6">
      <div class="col-12">
        <div class="card height-equal-2">
          <div class="card-header">
            <h2> Dispatch History </h2>
          </div>
          <div class="card-body">
            <DataTable :ajax="{ url: dispatchHistoryUrl }" ref="dispatchHistory"
                       class="table-responsive display table-borderless" :columns="dispatchColumn"/>
          </div>
        </div>
      </div>
    </div>

    <div class="col-sm-6">
      <div class="col-12">
        <DispatchDriverLists :service-provider-id="serviceProviderId"
                             @draw-driver-on-map="findDriver"></DispatchDriverLists>
      </div>
    </div>
  </div>

</template>

<script setup>
// Google Maps API is loaded via script tag
import {ref, onMounted, watch, nextTick} from "vue";
import DispatchForm from "./DispatchForm.vue";
import DispatchDriverLists from "./DispatchDriverLists.vue";
import DataTable from 'datatables.net-vue3';
import DataTablesCore from 'datatables.net-bs5';
import {zonedDateTime, potentialUndefinedColumn, rideStatus} from '../utility/DatatableRenderer';
import 'datatables.net-plugins/dataRender/ellipsis.mjs';

DataTable.use(DataTablesCore);


const props = defineProps({
  googleMapsApiKey: {type: String, required: true},
  serviceProviderId: {type: String, required: true}
});

const pickUpData = ref('');
const dropOffData = ref('');
const pickUpCoordinates = ref(null);
const dropOffCoordinates = ref(null);
const markers = ref([]);

const formReseter = ref(true);
const initialCoordinates = {lat: 46.255803, lng: -63.136968};
const zoomLevel = 11;
let map = null;
let googleMaps = null; // Google Maps API reference
let pickUpMarker = null;
let dropOffMarker = null;

const dispatchHistory = ref(null);
const dispatchHistoryUrl = '/api/datatable/dispatch-history';
const dispatchColumn = [
  {data: "driver.fullName", title: 'Driver', render: potentialUndefinedColumn},
  {data: "originLocationData.address", title: 'Pickup', render: DataTablesCore.render.ellipsis(20, false)},
  {data: "rideStatus", title: 'Status', render: rideStatus},
  {data: "orderDateTime", title: 'Dispatch Time', render: zonedDateTime},
  {
    title: '', orderable: false, searchable: false, render: (data, type, row, meta) => {
      return `<a class="btn btn-sm btn-outline-primary" target="blank" href="/service-provider/${props.serviceProviderId}/ride/${row.id}">
                Details <i class="fa-solid fa-arrow-up-right-from-square"></i></a>`;
    }
  }
];

//reload table every 10 seconds
setInterval(function () {
  dispatchHistory.value.dt.ajax.reload();
}, 10000);


const initMap = () => {
  // Load Google Maps API
  if (typeof google === 'undefined' || typeof google.maps === 'undefined') {
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${props.googleMapsApiKey}&loading=async&libraries=marker&callback=initGoogleMap`;
    script.async = true;
    script.defer = true;
    document.head.appendChild(script);

    // Define the callback function globally
    window.initGoogleMap = () => {
      createMap();
    };
  } else {
    createMap();
  }
};

const createMap = () => {
  googleMaps = google.maps;
  map = new googleMaps.Map(document.getElementById("map"), {
    center: initialCoordinates,
    zoom: zoomLevel,
    mapTypeControl: true,
    streetViewControl: true,
    fullscreenControl: true,
    mapId: 'f5f337b511d9a28bc10fc480',
  });
};


const drawPickupMarker = (geoResponse) => {
  //clear previous markers
  if (pickUpMarker) {
    pickUpMarker.map = null;
    pickUpMarker = null;
  }

  if (geoResponse?.geometry?.location) {
    // Handle response from geocodeByPlaceId
    const coordinates = {
      lat: geoResponse.geometry.location.lat,
      lng: geoResponse.geometry.location.lng
    };

    // Store coordinates for route calculation
    pickUpCoordinates.value = coordinates;

    const passengerIcon = document.createElement('img');
    passengerIcon.src = '/assets/images/man.png';
    passengerIcon.style.width = '30px';
    passengerIcon.style.height = '30px';

    pickUpMarker = new googleMaps.marker.AdvancedMarkerElement({
      position: coordinates,
      map: map,
      content: passengerIcon,
    });

    map.panTo(coordinates);
    map.setZoom(15);
  }
};

const drawDropOffMarker = (geoResponse) => {
  //clear previous markers
  if (dropOffMarker) {
    dropOffMarker.map = null;
    dropOffMarker = null;
  }

  if (geoResponse?.geometry?.location) {
    // Handle response from geocodeByPlaceId
    const coordinates = {
      lat: geoResponse.geometry.location.lat,
      lng: geoResponse.geometry.location.lng
    };

    // Store coordinates for route calculation
    dropOffCoordinates.value = coordinates;

    const destinationIcon = document.createElement('img');
    destinationIcon.src = '/assets/images/img.png';
    destinationIcon.style.width = '30px';
    destinationIcon.style.height = '30px';

    dropOffMarker = new googleMaps.marker.AdvancedMarkerElement({
      position: coordinates,
      map: map,
      content: destinationIcon,
    });
  }
};

const calculateRoute = async (origin, destination) => {
  try {
    const response = await fetch(
        `/api/dispatch-ride/calculate-route?origin=${encodeURIComponent(
            origin
        )}&destination=${encodeURIComponent(destination)}`
    );

    if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
    return await response.json();
  } catch (error) {
    console.error("Error calculating route:", error);
  }
};

let directionsRenderer = null;

const drawRoute = async () => {
  if (pickUpCoordinates.value && dropOffCoordinates.value) {
    // Clear previous route if exists
    if (directionsRenderer) {
      directionsRenderer.setMap(null);
    }

    // Create new DirectionsRenderer
    directionsRenderer = new googleMaps.DirectionsRenderer({
      map: map,
      suppressMarkers: true, // We're using our own markers
      polylineOptions: {
        strokeColor: '#4a90e2',
        strokeWeight: 6
      }
    });

    // Get route from backend
    const geojson = await calculateRoute(
        `${pickUpCoordinates.value.lat},${pickUpCoordinates.value.lng}`,
        `${dropOffCoordinates.value.lat},${dropOffCoordinates.value.lng}`
    );

    // If we have a valid GeoJSON response, convert it to Google Maps format
    if (geojson && geojson.geometry && geojson.geometry.coordinates) {
      const path = geojson.geometry.coordinates.map(coord => {
        return new googleMaps.LatLng(coord[1], coord[0]); // Convert [lng, lat] to LatLng
      });

      // Create a minimal DirectionsResult object
      const directionsResult = {
        request: {
          origin: new googleMaps.LatLng(pickUpCoordinates.value.lat, pickUpCoordinates.value.lng),
          destination: new googleMaps.LatLng(dropOffCoordinates.value.lat, dropOffCoordinates.value.lng),
          travelMode: googleMaps.TravelMode.DRIVING // Use enum
        },
        routes: [{
          legs: [{
            steps: [{
              path: path,
              travel_mode: googleMaps.TravelMode.DRIVING // Use enum
            }],
            start_location: path[0], // Already a LatLng object
            end_location: path[path.length - 1], // Already a LatLng object
            distance: {text: "0 km", value: 0}, // Placeholder
            duration: {text: "0 mins", value: 0}, // Placeholder
            travel_mode: googleMaps.TravelMode.DRIVING
          }],
          overview_path: path,
          bounds: new googleMaps.LatLngBounds().extend(path[0]).extend(path[path.length - 1])
        }]
      };

      try {
        directionsRenderer.setDirections(directionsResult);
      } catch (error) {
        console.error("Error setting directions:", error);
        // Fallback to drawing a polyline
        const polyline = new googleMaps.Polyline({
          path: path,
          strokeColor: '#4a90e2',
          strokeWeight: 6,
          map: map
        });
      }

      // Fit bounds to show the entire route
      const bounds = new googleMaps.LatLngBounds();
      path.forEach(point => bounds.extend(point));
      map.fitBounds(bounds);
    }
  }
};


const getGeocodeByPlaceId = async (placeId) => {
  try {
    const response = await fetch(
        `/api/dispatch-ride/geocode-by-place-id?placeId=${placeId}`
    );
    if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
    return await response.json();
  } catch (error) {
    console.error("Error fetching geocode by place ID:", error);
  }
};


const drawDriver = (driver) => {
  console.log(driver);
  const position = {lat: driver.latitude, lng: driver.longitude};

  const carIcon = document.createElement('img');
  carIcon.src = '/assets/images/car.png';
  carIcon.style.width = '30px';
  carIcon.style.height = '30px';

  // Create marker
  const marker = new googleMaps.marker.AdvancedMarkerElement({
    position: position,
    map: map,
    content: carIcon,
  });
  marker.id = `driver-${driver.id}`; // Store ID for later reference

  // Create info window (popup)
  const popupHtml = `
    <div style="text-align: left;">
      <h4>Driver Details</h4>
      <p><strong>Name:</strong> ${driver.name}</p>
      <p><strong>Vehicle:</strong> ${driver.vehicleType}</p>
      <p><strong>ETA:</strong> ${driver.eta || "N/A"}</p>
      <p><strong>Cab Size:</strong> ${driver.cabSize || "N/A"}</p>
      <p><strong>Status:</strong> ${driver.driverStatus}</p>
    </div>`;

  const infoWindow = new googleMaps.InfoWindow({
    content: popupHtml
  });

  // Add click event to open info window
  marker.addListener('click', () => {
    infoWindow.open({ anchor: marker, map });
  });

  markers.value.push(marker);
};

const drawAllDrivers = async () => {
  try {
    //todo use proper endpoint
    const response = await fetch("/api/datatable/driver-list");
    if (!response.ok) throw new Error(`Failed to fetch drivers: ${response.status}`);
    const drivers = await response.json();

    // Clear existing markers
    markers.value.forEach((marker) => { marker.map = null; });
    markers.value = [];

    drivers.data.forEach((driver) => {
      if (driver.latitude && driver.longitude) {
        drawDriver(driver);
      }
    });
  } catch (error) {
    console.error("Error fetching or drawing drivers:", error);
  }
};

const findDriver = (driver) => {
  console.log("Finding driver:", driver);
  // Find the marker for this driver
  const driverMarker = markers.value.find(marker => marker.id === `driver-${driver.id}`);

  if (driverMarker) {
    const mapElement = document.querySelector('#map');

    if (mapElement) {
      mapElement.scrollIntoView({behavior: 'smooth', block: 'center'});
    } else {
      console.warn("Map element not found!");
    }

    const position = {lat: driver.latitude, lng: driver.longitude};

    // Center the map on the driver
    map.panTo(position);
    map.setZoom(15);

    // Trigger a click on the marker to open the info window
    googleMaps.event.trigger(driverMarker, 'click');
  }
};

const resetDispatchForm = () => {
  formReseter.value = false;
  nextTick(() => {
    formReseter.value = true;
  });
}


onMounted(async () => {
  initMap();
  await drawAllDrivers();
});

// Watchers
watch(pickUpData, (newVal) => {
  if (newVal.result && newVal.result.id) {
    getGeocodeByPlaceId(newVal.result.id).then((response) => {
      drawPickupMarker(response.results[0]);
      drawRoute();
    });
  }
});

watch(dropOffData, (newVal) => {
  if (newVal.result && newVal.result.id) {
    getGeocodeByPlaceId(newVal.result.id).then((response) => {
      drawDropOffMarker(response.results[0]);
      drawRoute();
    });
  }
});
</script>
